/**
 * CORE-BN — Front-of-House Attention Capacity · shop settings
 *
 * The shop-facing surface for CORE-BI's `foh_attention_policy`. Lives on
 * the Shop Culture tab because that is where shop operating preferences
 * belong constitutionally, and it persists into the same
 * `shop_profile.shop_culture` object every other culture setting uses —
 * merged, never overwritten, so no other capability's keys are lost.
 *
 * Tone and framing
 * ----------------
 * These are estimates of how a shop's own morning works, and they are
 * presented that way. There is no implementation vocabulary on this
 * screen — no "claims", no "weights", no "custody states" — only
 * questions an owner can answer from experience.
 *
 * The framing is not decoration. The constitution is explicit that this
 * capability "must never allow a capacity estimate to become a judgment
 * about the individual standing at the desk", and a settings screen that
 * read like a productivity standard would invite exactly that reading.
 * So the card says plainly, twice, what these numbers are and what they
 * are not.
 *
 * Safety
 * ------
 * Validation runs against `FOH_POLICY_BOUNDS` — the same record the model
 * enforces at read time — so this screen can never accept a value the
 * model would silently discard. In particular a shop cannot set a
 * meaningful demand to zero: the field is rejected with an explanation
 * rather than saved and quietly reverted.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Info, Loader2, Users } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useFeatureFlag } from '@/hooks/useFeatureFlags';
import { toast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  readFohAttentionPolicyBlob,
  resolveFohAttentionPolicy,
  FOH_CONFIGURABLE_CLAIM_KINDS,
} from '@/lib/foh/attention/policy';
import { persistShopCulturePatch } from '@/lib/shopCulture/persistShopCulturePatch';
import {
  defaultFohAttentionPolicyForm,
  fohPolicyCulturePatch,
  FOH_POLICY_REMOVAL_PATCH,
  policyToForm,
  validateFohAttentionPolicyForm,
  FOH_CLAIM_FORM_LABELS,
  FOH_SENSITIVITY_LABELS,
  FOH_SENSITIVITY_PRESETS,
  type FohAttentionPolicyForm,
  type FohSaturationSensitivity,
} from '@/lib/foh/attention/policyForm';

interface FohAttentionSettingsCardProps {
  /** False for roles that may view the shop profile but not change it. */
  canEdit?: boolean;
}

export function FohAttentionSettingsCard({
  canEdit = true,
}: FohAttentionSettingsCardProps) {
  const { activeShop } = useAuth();
  const enabled = useFeatureFlag('fohAttentionCapacity');
  const shopId = activeShop?.id ?? null;

  const [form, setForm] = useState<FohAttentionPolicyForm>(
    defaultFohAttentionPolicyForm,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const errors = useMemo(() => validateFohAttentionPolicyForm(form), [form]);
  const hasErrors = Object.keys(errors).length > 0;

  const load = useCallback(async () => {
    if (!shopId || !enabled) {
      setIsLoading(false);
      return;
    }
    try {
      const { data, error } = await supabase
        .from('shop_profile')
        .select('shop_culture')
        .eq('shop_id', shopId)
        .maybeSingle();
      if (error) throw error;
      const policy = resolveFohAttentionPolicy(
        readFohAttentionPolicyBlob(data?.shop_culture ?? null),
      );
      setForm(policyToForm(policy));
    } catch (err) {
      console.error('[core-bn settings] load failed', err);
      // Leave the conservative defaults in place rather than showing a
      // half-loaded form that misrepresents what the model is using.
    } finally {
      setIsLoading(false);
    }
  }, [shopId, enabled]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Persist the CORE-BN key, leaving every other capability's key alone.
   *
   * `mode: 'save'` writes the form's policy. `mode: 'reset'` REMOVES the
   * key so the shop returns to genuinely untuned state — see
   * `removeFohPolicyFromShopCulture` for why removal rather than writing
   * the default values back.
   *
   * A failed read aborts before writing: patching onto an unknown
   * baseline is how shared JSON columns get erased.
   */
  const persist = async (mode: 'save' | 'reset') => {
    if (!shopId) return;
    if (mode === 'save' && hasErrors) return;
    setIsSaving(true);
    try {
      // CORE-BI: the one key CORE-BN owns, set or removed by the server-side
      // patch (patch_shop_culture) — no browser baseline, no lost concurrent save.
      await persistShopCulturePatch(
        supabase,
        shopId,
        mode === 'reset' ? FOH_POLICY_REMOVAL_PATCH : fohPolicyCulturePatch(form),
      );

      if (mode === 'reset') setForm(defaultFohAttentionPolicyForm());

      toast({
        title:
          mode === 'reset'
            ? 'Reset to OptiBay defaults'
            : 'Front-of-house settings saved',
        description:
          mode === 'reset'
            ? 'This shop no longer overrides the front-of-house model.'
            : 'New estimates will use these values.',
      });
    } catch (err) {
      console.error(`[core-bn settings] ${mode} failed`, err);
      toast({
        title: mode === 'reset' ? 'Could not reset' : 'Could not save',
        description: 'Front-of-house settings were not changed.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (!enabled) return null;

  const disabled = !canEdit || isSaving || isLoading;

  const setNumber = (key: keyof FohAttentionPolicyForm, raw: string) => {
    const parsed = raw.trim() === '' ? Number.NaN : Number(raw);
    setForm((f) => ({ ...f, [key]: parsed }));
  };

  return (
    <Card data-testid="foh-attention-settings-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          Front-of-House Load
        </CardTitle>
        <CardDescription>
          How busy your front desk has to get before OptiBay says so. These are
          estimates of how your shop runs — they are never a measure of how
          anyone is performing, and OptiBay will never use them to rank, score
          or compare people.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading current settings…
          </div>
        ) : (
          <>
            {/* --- The desk ------------------------------------------- */}
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                id="foh-desk-time"
                label="Time really available at the desk"
                help="Out of any given stretch, how much of a front-desk person's time is genuinely free for customers, calls and tickets? The rest goes to everything else a shop day contains."
                suffix="%"
                value={form.deskTimeAvailablePercent}
                error={errors.deskTimeAvailablePercent}
                disabled={disabled}
                onChange={(v) => setNumber('deskTimeAvailablePercent', v)}
              />
              <Field
                id="foh-lobby"
                label="A customer waiting in the lobby"
                help="Roughly what share of one person's attention a waiting customer occupies — checking in on them, updating them, keeping them in mind."
                suffix="% of one person"
                value={form.lobbyCustomerAttentionPercent}
                error={errors.lobbyCustomerAttentionPercent}
                disabled={disabled}
                onChange={(v) => setNumber('lobbyCustomerAttentionPercent', v)}
              />
              <Field
                id="foh-window"
                label="Look ahead"
                help="How far forward each estimate looks."
                suffix="minutes"
                value={form.windowMinutes}
                error={errors.windowMinutes}
                disabled={disabled}
                onChange={(v) => setNumber('windowMinutes', v)}
              />
              <div className="space-y-1.5">
                <Label htmlFor="foh-headcount">Front-of-house team size</Label>
                <Input
                  id="foh-headcount"
                  inputMode="numeric"
                  placeholder="Count who is clocked in"
                  disabled={disabled}
                  value={
                    form.staffHeadcountOverride === null ||
                    form.staffHeadcountOverride === undefined
                      ? ''
                      : String(form.staffHeadcountOverride)
                  }
                  onChange={(e) => {
                    const raw = e.target.value.trim();
                    setForm((f) => ({
                      ...f,
                      staffHeadcountOverride: raw === '' ? null : Number(raw),
                    }));
                  }}
                />
                <p className="text-xs text-muted-foreground">
                  Leave blank and OptiBay counts who is actually clocked in to a
                  front-of-house role. Set a number only if that count does not
                  reflect your shop.
                </p>
                {errors.staffHeadcountOverride && (
                  <p className="text-xs text-destructive">
                    {errors.staffHeadcountOverride}
                  </p>
                )}
              </div>
            </div>

            <Separator />

            {/* --- Sensitivity ---------------------------------------- */}
            <div className="space-y-1.5">
              <Label htmlFor="foh-sensitivity">When should OptiBay speak up?</Label>
              <Select
                value={form.sensitivity}
                disabled={disabled}
                onValueChange={(v) =>
                  setForm((f) => ({
                    ...f,
                    sensitivity: v as FohSaturationSensitivity,
                  }))
                }
              >
                <SelectTrigger id="foh-sensitivity">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(
                    Object.keys(FOH_SENSITIVITY_PRESETS) as FohSaturationSensitivity[]
                  ).map((key) => (
                    <SelectItem key={key} value={key}>
                      {FOH_SENSITIVITY_LABELS[key].label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {FOH_SENSITIVITY_LABELS[form.sensitivity]?.description}
              </p>
            </div>

            <Separator />

            {/* --- Demand -------------------------------------------- */}
            <div className="space-y-3">
              <div>
                <h4 className="text-sm font-medium">
                  How long these take your front desk
                </h4>
                <p className="text-xs text-muted-foreground">
                  Best estimates, in minutes. They do not have to be exact — they
                  only need to reflect which parts of your day cost more than
                  others.
                </p>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {FOH_CONFIGURABLE_CLAIM_KINDS.map((kind) => (
                  <Field
                    key={kind}
                    id={`foh-claim-${kind}`}
                    label={FOH_CLAIM_FORM_LABELS[kind]?.label ?? kind}
                    help={FOH_CLAIM_FORM_LABELS[kind]?.help ?? ''}
                    suffix="min"
                    value={form.claimMinutes?.[kind]}
                    error={errors[`claimMinutes.${kind}`]}
                    disabled={disabled}
                    onChange={(raw) =>
                      setForm((f) => ({
                        ...f,
                        claimMinutes: {
                          ...f.claimMinutes,
                          [kind]: raw.trim() === '' ? Number.NaN : Number(raw),
                        },
                      }))
                    }
                  />
                ))}
              </div>
            </div>

            <div className="flex items-start gap-1.5 rounded-md border border-border/60 bg-muted/40 p-2.5 text-xs text-muted-foreground">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              <span>
                OptiBay uses these to estimate how loaded the front of the shop
                is, and shows the reasons behind every estimate. It is advisory:
                it will never turn away a customer, refuse a booking or move
                anyone&apos;s work on its own.
              </span>
            </div>

            {canEdit && (
              <div className="flex items-center gap-3">
                <Button onClick={() => persist('save')} disabled={disabled || hasErrors}>
                  {isSaving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving…
                    </>
                  ) : (
                    'Save front-of-house settings'
                  )}
                </Button>
                <Button
                  variant="ghost"
                  disabled={disabled}
                  onClick={() => persist('reset')}
                >
                  Reset to OptiBay defaults
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function Field({
  id,
  label,
  help,
  suffix,
  value,
  error,
  disabled,
  onChange,
}: {
  id: string;
  label: string;
  help: string;
  suffix: string;
  value: number | undefined;
  error?: string;
  disabled?: boolean;
  onChange: (raw: string) => void;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <div className="flex items-center gap-2">
        <Input
          id={id}
          inputMode="decimal"
          className="max-w-[9rem]"
          disabled={disabled}
          value={
            value === undefined || value === null || Number.isNaN(value)
              ? ''
              : String(value)
          }
          onChange={(e) => onChange(e.target.value)}
          aria-invalid={!!error}
        />
        <span className="text-xs text-muted-foreground">{suffix}</span>
      </div>
      {help && <p className="text-xs text-muted-foreground">{help}</p>}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
